import type { ReactNode } from 'react';
import { useMemo } from 'react';
import type { LayoutResult } from './layout-treemap';
import { parseMermaidTreemap } from './parse-mermaid-treemap';
import { getTreemapCompactPreview } from './TreemapCompactPreview';
import type { TreemapHoverState } from './treemap-hover-context';
import type { ParsedTreemap, TreemapFlowNode } from './treemap-types';
import {
  getTreemapActiveNodeIds,
  getTreemapLayout,
  getTreemapLayoutNodes,
  getTreemapParentMap,
  getTreemapRectById,
  getTreemapTotalValue,
  type TreemapViewportRect,
} from './treemap-view-model';

interface UseTreemapRenderModelInput {
  content: string;
  hoveredNodeId: string | null;
}

export interface TreemapRenderModel {
  parsed: ParsedTreemap | null;
  layout: LayoutResult | null;
  layoutNodes: TreemapFlowNode[];
  parentMap: Map<string, string>;
  rectById: Map<string, TreemapViewportRect>;
  interactionState: TreemapHoverState;
  compactPreview: ReactNode;
  totalValue: number;
}

export function useTreemapRenderModel({
  content,
  hoveredNodeId,
}: UseTreemapRenderModelInput): TreemapRenderModel {
  const parsed = useMemo(() => parseMermaidTreemap(content), [content]);
  const layout = useMemo(() => getTreemapLayout(parsed), [parsed]);
  const layoutNodes = getTreemapLayoutNodes(layout);
  const parentMap = useMemo(() => getTreemapParentMap(parsed), [parsed]);
  const rectById = useMemo(() => getTreemapRectById(layoutNodes), [layoutNodes]);
  const activeNodeIds = useMemo(
    () => getTreemapActiveNodeIds({ hoveredNodeId, parsed, parentMap }),
    [hoveredNodeId, parsed, parentMap],
  );
  const interactionState = useMemo<TreemapHoverState>(
    () => ({ hoveredNodeId, activeNodeIds }),
    [hoveredNodeId, activeNodeIds],
  );
  const compactPreview = useMemo(() => getTreemapCompactPreview(parsed), [parsed]);
  const totalValue = getTreemapTotalValue(parsed);

  return {
    parsed,
    layout,
    layoutNodes,
    parentMap,
    rectById,
    interactionState,
    compactPreview,
    totalValue,
  };
}
